import {
  type HaciendaCheck,
  type HaciendaCredentialsInput,
  type HaciendaSettings,
  haciendaCredentialsInput,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  FileKeyIcon,
  KeyRoundIcon,
  LockIcon,
  PlugZapIcon,
  ShieldCheckIcon,
  UploadCloudIcon,
  XCircleIcon,
} from 'lucide-react'
import { m } from 'motion/react'
import { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { TRANSITION } from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { CertificateAlert } from '@/features/settings/certificate-alert'
import { api } from '@/lib/api'
import { formatDate, formatIdentification, formatRelative } from '@/lib/format'
import { companyQuery } from '@/lib/queries'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/settings/hacienda')({
  staticData: { crumb: 'Hacienda' },
  component: HaciendaSettingsPage,
})

const settingsQuery = queryOptions({
  queryKey: ['hacienda-settings'],
  queryFn: () => api.get<HaciendaSettings>('/hacienda/settings'),
})

function HaciendaSettingsPage() {
  const { data, isLoading } = useQuery(settingsQuery)
  const canManage = useCan('settings:manage')
  const [check, setCheck] = useState<HaciendaCheck | null>(null)
  const company = useQuery(companyQuery())
  const runCheck = useMutation({
    mutationFn: () => api.post<HaciendaCheck>('/hacienda/check'),
    onSuccess: (result) => setCheck(result),
  })

  if (isLoading || !data) return <Skeleton className="h-96 rounded-xl" />

  return (
    <div className="grid gap-6">
      <CertificateAlert link={false} />
      {company.data?.haciendaEnabled === false && (
        <div className="flex items-start gap-3 rounded-xl border border-info/25 bg-info-soft px-4 py-3 text-info-soft-foreground text-sm">
          <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
          <span>
            Esta compañía emite <strong>comprobantes internos</strong>: se numeran y se envían por correo,
            pero no se transmiten a Hacienda. Un administrador de la plataforma puede activar el envío.
          </span>
        </div>
      )}
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
          <div
            className={cn(
              'flex size-12 shrink-0 items-center justify-center rounded-2xl',
              data.ready ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning',
            )}
          >
            {data.ready ? <ShieldCheckIcon className="size-6" /> : <AlertTriangleIcon className="size-6" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold">
                {data.ready ? 'Listo para emitir' : 'Configuración pendiente'}
              </h2>
              <Badge variant={data.environment === 'production' ? 'success' : 'warning'}>
                {data.environment === 'production' ? 'Producción' : 'Pruebas (sandbox)'}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              {data.ready
                ? data.lastCheck
                  ? `Última verificación ${formatRelative(data.lastCheck.at)}: ${data.lastCheck.ok ? 'todo en orden' : data.lastCheck.message}`
                  : 'Verificá la conexión antes de emitir tu primer comprobante.'
                : 'Cargá la llave criptográfica y las credenciales de ATV para firmar y enviar comprobantes.'}
            </p>
          </div>
          {canManage && (
            <Button
              variant="outline"
              loading={runCheck.isPending}
              disabled={!data.certificate && !data.username}
              onClick={() => runCheck.mutate()}
            >
              <PlugZapIcon /> Probar conexión
            </Button>
          )}
        </div>
        {check && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={TRANSITION.collapse}
            className="grid grid-cols-1 gap-2 border-border border-t bg-subtle px-5 py-4 sm:grid-cols-2"
          >
            <CheckRow title="Llave criptográfica" result={check.certificate} />
            <CheckRow title="Credenciales ATV" result={check.credentials} />
          </m.div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CertificateCard settings={data} canManage={canManage} />
        <CredentialsCard settings={data} canManage={canManage} />
      </div>
    </div>
  )
}

function CheckRow({ title, result }: { title: string; result: { ok: boolean; message: string } }) {
  return (
    <div className="flex items-start gap-2.5 text-sm">
      {result.ok ? (
        <CheckCircle2Icon className="mt-0.5 size-4 text-success" />
      ) : (
        <XCircleIcon className="mt-0.5 size-4 text-destructive" />
      )}
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-muted-foreground">{result.message}</div>
      </div>
    </div>
  )
}

function CertificateCard({ settings, canManage }: { settings: HaciendaSettings; canManage: boolean }) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [pin, setPin] = useState('')
  const [dragging, setDragging] = useState(false)
  const cert = settings.certificate
  const daysLeft = cert ? Math.floor((new Date(cert.validTo).getTime() - Date.now()) / 86_400_000) : null

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Elegí el archivo .p12')
      const bytes = new Uint8Array(await file.arrayBuffer())
      let binary = ''
      for (const b of bytes) binary += String.fromCharCode(b)
      return api.put<HaciendaSettings>('/hacienda/certificate', { fileBase64: btoa(binary), pin })
    },
    onSuccess: (data) => {
      queryClient.setQueryData(settingsQuery.queryKey, data)
      setFile(null)
      setPin('')
      toast.success('Llave criptográfica cargada')
    },
  })

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileKeyIcon className="size-4 text-muted-foreground" /> Llave criptográfica
          </CardTitle>
          <CardDescription>
            Archivo .p12 descargado de ATV y su PIN de 4 dígitos. Se guardan cifrados.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="grid flex-1 gap-4">
        {cert && (
          <div className="rounded-xl border border-border bg-subtle p-4 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-medium">{cert.subject}</div>
                <div className="text-muted-foreground text-xs">Emitida por {cert.issuer}</div>
              </div>
              <Badge variant={daysLeft !== null && daysLeft < 30 ? 'warning' : 'success'}>
                {daysLeft !== null && daysLeft < 0 ? 'Vencida' : `${daysLeft} días`}
              </Badge>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-muted-foreground">Identificación</div>
                <div className="font-mono">
                  {cert.identification
                    ? formatIdentification(
                        cert.identification.length === 9 ? '01' : '02',
                        cert.identification,
                      )
                    : '—'}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Vigencia</div>
                <div>
                  {formatDate(cert.validFrom)} – {formatDate(cert.validTo)}
                </div>
              </div>
            </div>
            {!cert.matchesCompany && (
              <p className="mt-3 flex items-center gap-1.5 text-warning-soft-foreground text-xs">
                <AlertTriangleIcon className="size-3.5" /> La cédula de la llave no coincide con la de la
                compañía.
              </p>
            )}
          </div>
        )}
        {canManage && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragging(false)
                const dropped = e.dataTransfer.files[0]
                if (dropped) setFile(dropped)
              }}
              className={cn(
                'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-7 text-center transition',
                dragging
                  ? 'border-primary bg-primary-soft/50'
                  : 'border-border hover:border-foreground/25 hover:bg-accent/40',
              )}
            >
              <UploadCloudIcon className={cn('size-7', file ? 'text-primary' : 'text-muted-foreground')} />
              <span className="font-medium text-sm">
                {file ? file.name : cert ? 'Reemplazar llave' : 'Arrastrá tu archivo .p12'}
              </span>
              <span className="text-muted-foreground text-xs">
                {file ? `${(file.size / 1024).toFixed(1)} KB` : 'o hacé clic para buscarlo'}
              </span>
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".p12,.pfx,application/x-pkcs12"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Field label="PIN de la llave">
              {(c) => (
                <Input
                  {...c}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  inputMode="numeric"
                  type="password"
                  placeholder="••••"
                  leading={<LockIcon />}
                  className="max-w-40"
                />
              )}
            </Field>
          </>
        )}
      </CardContent>
      {canManage && (
        <CardFooter>
          <Button
            disabled={!file || pin.length !== 4}
            loading={upload.isPending}
            onClick={() => upload.mutate()}
          >
            <UploadCloudIcon /> {cert ? 'Reemplazar llave' : 'Cargar llave'}
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}

function CredentialsCard({ settings, canManage }: { settings: HaciendaSettings; canManage: boolean }) {
  const queryClient = useQueryClient()
  const form = useForm<HaciendaCredentialsInput>({
    resolver: zodResolver(haciendaCredentialsInput),
    values: { environment: settings.environment, username: settings.username ?? '', password: '' },
  })
  const save = useMutation({
    mutationFn: (values: HaciendaCredentialsInput) =>
      api.put<HaciendaSettings>('/hacienda/credentials', values),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsQuery.queryKey, data)
      toast.success('Credenciales guardadas')
    },
  })
  const environment = form.watch('environment')

  return (
    <Card className="flex flex-col">
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate className="flex flex-1 flex-col">
        <CardHeader>
          <div>
            <CardTitle className="flex items-center gap-2">
              <KeyRoundIcon className="size-4 text-muted-foreground" /> Credenciales ATV
            </CardTitle>
            <CardDescription>Usuario y contraseña del API de comprobantes generados en ATV.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="grid flex-1 content-start gap-4">
          <Controller
            control={form.control}
            name="environment"
            render={({ field }) => (
              <Field label="Ambiente">
                {() => (
                  <div className="grid grid-cols-2 gap-2">
                    {(
                      [
                        ['staging', 'Pruebas', 'Sandbox de Hacienda, sin validez fiscal'],
                        ['production', 'Producción', 'Comprobantes con validez fiscal'],
                      ] as const
                    ).map(([value, label, hint]) => (
                      <button
                        key={value}
                        type="button"
                        disabled={!canManage}
                        onClick={() => field.onChange(value)}
                        className={cn(
                          'rounded-xl border p-3 text-left transition',
                          field.value === value
                            ? 'border-primary/60 bg-primary-soft/60 ring-2 ring-primary/15'
                            : 'border-border hover:bg-accent/50',
                        )}
                      >
                        <div className="font-medium text-sm">{label}</div>
                        <div className="text-muted-foreground text-xs">{hint}</div>
                      </button>
                    ))}
                  </div>
                )}
              </Field>
            )}
          />
          <Field label="Usuario" error={form.formState.errors.username}>
            {(c) => (
              <Input
                {...c}
                {...form.register('username')}
                disabled={!canManage}
                placeholder={
                  environment === 'production'
                    ? 'cpj-3-101-000000@prod.comprobanteselectronicos.go.cr'
                    : 'cpj-3-101-000000@stag.comprobanteselectronicos.go.cr'
                }
                className="font-mono text-xs"
              />
            )}
          </Field>
          <Field
            label="Contraseña"
            hint={settings.hasPassword ? 'Dejala vacía para conservar la guardada.' : undefined}
            error={form.formState.errors.password}
          >
            {(c) => (
              <Input
                {...c}
                {...form.register('password')}
                type="password"
                disabled={!canManage}
                placeholder={settings.hasPassword ? '••••••••••••' : ''}
                autoComplete="new-password"
              />
            )}
          </Field>
        </CardContent>
        {canManage && (
          <CardFooter>
            <Button type="submit" loading={save.isPending}>
              Guardar credenciales
            </Button>
          </CardFooter>
        )}
      </form>
    </Card>
  )
}
